// src/pages/ForColleges.js - WITH ANIMATED HERO BACKGROUND
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ForColleges.css';
import Footer from '../components/Footer';
import AnimatedHeroBackground from '../components/AnimatedHeroBackground';

const ForColleges = () => {
  const [formData, setFormData] = useState({
    collegeName: '',
    contactPerson: '',
    designation: '',
    email: '',
    phone: '',
    studentsCount: '',
    program: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you soon.');
  };

  const services = [
    {
      id: 1,
      icon: '🎓',
      title: 'Integrated Sessions',
      duration: '300-600+ Hours',
      description: 'Comprehensive semester-long training programs with placement guidance and industry mentorship',
      features: [
        'Full semester training (1-2 years)',
        'Placement guidance & support',
        'Joint certification with iNikola',
        'Real-world project experience',
        'Industry mentor guidance',
        'Free EVALSEA platform access'
      ],
      stats: {
        students: '500+',
        duration: '1-2 Years',
        certification: 'Yes'
      },
      link: '/integrated-sessions',
      color: '#ff6b35'
    },
    {
      id: 2,
      icon: '🔬',
      title: 'Workshops',
      duration: '1-15 Days',
      description: 'Intensive hands-on workshops on AI, Data Science, and emerging technologies',
      features: [
        'Fully customizable curriculum',
        'Expert trainers from industry',
        'Hands-on practical labs',
        'Certificate of completion',
        'On-campus or online delivery',
        'Post-workshop support'
      ],
      stats: {
        students: '1000+',
        colleges: '10+',
        rating: '4.8/5'
      },
      link: '/workshops',
      color: '#f7931e'
    },
    {
      id: 3,
      icon: '🎯',
      title: 'Custom Training',
      duration: 'Tailored to You',
      description: 'Bespoke training programs designed specifically for your institution\'s needs',
      features: [
        'Your requirements, our expertise',
        'Flexible duration & format',
        'Department-specific training',
        'Ongoing support & consultation',
        'Faculty training included',
        'Curriculum integration support'
      ],
      stats: {
        programs: '25+',
        satisfaction: '100%',
        repeat: '100%'
      },
      link: '/custom-training',
      color: '#4caf50'
    }
  ];

  const benefits = [
    {
      icon: '📚',
      title: 'Curriculum Integration',
      description: 'Seamlessly integrate our programs into your existing curriculum with academic credit options',
      points: ['Credit transfer support', 'Syllabus alignment', 'Academic documentation']
    },
    {
      icon: '💼',
      title: 'Placement Guidance',
      description: 'Comprehensive placement support with connections to 50+ hiring companies',
      points: ['Resume building', 'Mock interviews', 'Career counseling']
    },
    {
      icon: '🤖',
      title: 'AI-Powered Tracking',
      description: 'Free EVALSEA platform for automated attendance, assessment, and progress tracking',
      points: ['Real-time analytics', 'Performance reports', 'Parent portal']
    },
    {
      icon: '🎓',
      title: 'Faculty Development',
      description: 'Train your faculty to continue the programs independently',
      points: ['Faculty workshops', 'Resource materials', 'Ongoing mentorship']
    },
    {
      icon: '🏆',
      title: 'Industry Recognition',
      description: 'Joint certificates from Crafting Brain & iNikola add value to student profiles',
      points: ['Verified credentials', 'LinkedIn integration', 'Industry endorsed']
    },
    {
      icon: '📊',
      title: 'Performance Analytics',
      description: 'Detailed reports on student progress, attendance, and outcomes',
      points: ['Weekly updates', 'Success metrics', 'ROI tracking']
    }
  ];

  const successStories = [
    {
      college: 'Sphoorthy Engineering College',
      duration: '18 Weeks Program',
      students: '120 Students',
      outcomes: 'Enhanced technical skills with hands-on AI/ML projects and comprehensive industry training',
      highlight: 'Students successfully built 10+ industry-level projects'
    },
    {
      college: 'IARE Engineering College',
      duration: '12 Weeks Workshop Series',
      students: '85 Students',
      outcomes: 'Comprehensive training in Data Science and Cloud Computing with practical applications',
      highlight: 'Faculty appreciated practical approach and student engagement'
    },
    {
      college: 'VNR Vignana Jyothi',
      duration: '6 Months Integrated Program',
      students: '95 Students',
      outcomes: 'Deep learning in AI, ML, and emerging technologies with real-world implementations',
      highlight: 'College received recognition for innovative curriculum integration'
    }
  ];

  const testimonials = [
    {
      name: 'Dr. Ramesh',
      position: 'Principal, Sphoorthy Engineering College',
      quote: 'After partnering with Crafting Brain, the efficiency of our students has improved significantly. The training quality was outstanding, and the learning experience exceeded expectations.',
      rating: 5
    },
    {
      name: 'Mr. Prasad',
      position: 'TPO, IARE Engineering College',
      quote: 'Crafting Brain delivered tremendous results. The sessions were impactful, practical, and industry-aligned. We are continuing our collaboration for upcoming projects with great confidence.',
      rating: 5
    },
    {
      name: 'Dr. Lalitha',
      position: 'HOD CSE, VNR Vignana Jyothi',
      quote: 'The quality of training and delivery by Crafting Brain has been excellent. Students appreciated the hands-on learning and clarity of concepts. We are excited to work together on long-term initiatives.',
      rating: 5
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery Call',
      description: 'Schedule a free consultation to discuss your institution\'s specific needs and goals',
      icon: '📞'
    },
    {
      step: '02',
      title: 'Custom Proposal',
      description: 'Receive a tailored proposal with curriculum, timeline, and investment details',
      icon: '📋'
    },
    {
      step: '03',
      title: 'Agreement & Onboarding',
      description: 'Sign partnership agreement and begin faculty coordination and student registration',
      icon: '✍️'
    },
    {
      step: '04',
      title: 'Program Delivery',
      description: 'Expert trainers deliver engaging sessions with continuous assessment and support',
      icon: '🎯'
    },
    {
      step: '05',
      title: 'Certification & Support',
      description: 'Award certificates, gather feedback, and provide post-program mentorship',
      icon: '🚀'
    }
  ];

  const impactMetrics = [
    {
      number: '1000+',
      label: 'Students Trained',
      description: 'Across multiple colleges'
    },
    {
      number: '10+',
      label: 'Partner Colleges',
      description: 'Leading engineering institutions'
    },
    {
      number: '100%',
      label: 'Repeated Collaborations',
      description: 'Long-term partnerships'
    },
    {
      number: '50+',
      label: 'Expert Trainers',
      description: 'Industry professionals'
    }
  ];

  return (
    <AnimatedHeroBackground>
      <div className="for-colleges-page">
        {/* Hero Section */}
        <section className="colleges-hero">
          <div className="hero-content">
            <div className="hero-badge">🏆 Trusted by 10+ Leading Engineering Colleges</div>
            <h1 className="hero-title">Empower Your Campus with Industry-Ready Training</h1>
            <p className="hero-subtitle">
              Partnering with colleges to transform education through AI, Data Science & Emerging Technologies. 
              We provide comprehensive training programs with industry mentorship and placement guidance.
            </p>
            <div className="hero-stats-mini">
              <div className="mini-stat">
                <span className="mini-stat-number">1000+</span>
                <span className="mini-stat-label">Students Trained</span>
              </div>
              <div className="mini-stat">
                <span className="mini-stat-number">10+</span>
                <span className="mini-stat-label">Partner Colleges</span>
              </div>
              <div className="mini-stat">
                <span className="mini-stat-number">100%</span>
                <span className="mini-stat-label">Satisfaction</span>
              </div>
            </div>
            <div className="hero-cta">
              <Link to="/contact" className="cta-primary">
                Schedule Campus Demo
                <span className="btn-icon">📅</span>
              </Link>
              <a href="/Brochure_A4_FullBleed_v2.pdf" download className="cta-secondary">
                Download Brochure
                <span className="btn-icon">📥</span>
              </a>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="colleges-intro">
          <div className="container">
            <div className="intro-content">
              <h2 className="intro-title">Transforming Colleges into Innovation Hubs</h2>
              <p className="intro-text">
                At <strong>Crafting Brain</strong>, we collaborate with colleges to bridge the gap between academia and industry 
                through specialized AI, Data Science, and emerging technology programs. Our mission is to transform your campus 
                into a hub of innovation and prepare students for the global stage with industry-ready skills and real-world project experience.
              </p>
            </div>
          </div>
        </section>

        

        {/* Services Section */}
        <section className="services-section">
          <div className="container">
            <h2 className="section-title">Our Services for Colleges</h2>
            <p className="section-subtitle">Choose the program that best fits your institution's needs</p>
            
            <div className="services-grid">
              {services.map((service) => (
                <div 
                  key={service.id} 
                  className="service-card"
                  style={{ '--service-color': service.color }}
                >
                  <div className="service-header">
                    <div className="service-icon-professional">
                      <svg className="icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {service.id === 1 && <path d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z M12 14l9-5-9-5-9 5 9 5z" />}
                        {service.id === 2 && <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />}
                        {service.id === 3 && <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />}
                      </svg>
                    </div>
                    <div className="service-duration">{service.duration}</div>
                  </div>
                  
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  
                  <div className="service-stats-mini">
                    {Object.entries(service.stats).map(([key, value]) => (
                      <div key={key} className="stat-mini">
                        <span className="stat-mini-value">{value}</span>
                        <span className="stat-mini-label">{key}</span>
                      </div>
                    ))}
                  </div>

                  <ul className="service-features">
                    {service.features.map((feature, index) => (
                      <li key={index}>
                        <span className="check-icon">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link to={service.link} className="service-link">
                    Learn More <span className="arrow">→</span>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits-section">
          <div className="container">
            <h2 className="section-title">What Your College Gets</h2>
            <p className="section-subtitle">Comprehensive support beyond just training</p>
            
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-card">
                  <div className="benefit-icon">{benefit.icon}</div>
                  <h3 className="benefit-title">{benefit.title}</h3>
                  <p className="benefit-description">{benefit.description}</p>
                  <ul className="benefit-points">
                    {benefit.points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="process-section">
          <div className="container">
            <h2 className="section-title">How We Partner with You</h2>
            <p className="section-subtitle">Simple 5-step process from inquiry to completion</p>
            
            <div className="process-timeline">
              {processSteps.map((item, index) => (
                <div key={index} className="process-step">
                  <div className="step-icon">{item.icon}</div>
                  <div className="step-number">{item.step}</div>
                  <h3 className="step-title">{item.title}</h3>
                  <p className="step-description">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="success-stories-section">
          <div className="container">
            <h2 className="section-title">Success Stories from Partner Colleges</h2>
            <p className="section-subtitle">Real results from real partnerships</p>
            
            <div className="success-grid">
              {successStories.map((story, index) => (
                <div key={index} className="success-card">
                  <h3 className="success-college">{story.college}</h3>
                  <div className="success-details">
                    <div className="success-detail-item">
                      <span className="detail-icon">⏱️</span>
                      <span className="detail-text">{story.duration}</span>
                    </div>
                    <div className="success-detail-item">
                      <span className="detail-icon">👥</span>
                      <span className="detail-text">{story.students}</span>
                    </div>
                  </div>
                  <p className="success-outcomes">{story.outcomes}</p>
                  <div className="success-highlight">
                    <span className="highlight-icon">🏆</span>
                    {story.highlight}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Metrics Section */}
        <section className="metrics-section-enhanced">
          <div className="container">
            <h2 className="section-title">Our Impact in Numbers</h2>
            <p className="section-subtitle">Delivering measurable results across institutions</p>
            <div className="metrics-grid-enhanced">
              {impactMetrics.map((metric, index) => (
                <div key={index} className="metric-item-enhanced">
                  <div className="metric-number-enhanced">{metric.number}</div>
                  <div className="metric-label-enhanced">{metric.label}</div>
                  <div className="metric-description">{metric.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="testimonials-section">
          <div className="container">
            <h2 className="section-title">What College Leaders Say</h2>
            
            <div className="testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="testimonial-card">
                  <div className="testimonial-rating">
                    {'⭐'.repeat(testimonial.rating)}
                  </div>
                  <div className="quote-icon">"</div>
                  <p className="testimonial-quote">{testimonial.quote}</p>
                  <div className="testimonial-author">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-position">{testimonial.position}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA with Form */}
        <section className="final-cta-section-with-form">
          <div className="container">
            <div className="cta-form-container">
              {/* Left Side - Form */}
              <div className="cta-form-wrapper">
                <h3 className="form-title">Request Campus Demo</h3>
                <p className="form-subtitle">Fill in your details and we'll contact you within 24 hours</p>
                
                <form onSubmit={handleSubmit} className="college-form">
                  <div className="form-group">
                    <label htmlFor="collegeName">College/Institution Name *</label>
                    <input
                      type="text"
                      id="collegeName"
                      name="collegeName"
                      value={formData.collegeName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter college name"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="contactPerson">Contact Person *</label>
                      <input
                        type="text"
                        id="contactPerson"
                        name="contactPerson"
                        value={formData.contactPerson}
                        onChange={handleInputChange}
                        required
                        placeholder="Your name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="designation">Designation *</label>
                      <input
                        type="text"
                        id="designation"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Principal, HOD"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="email@college.edu"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="studentsCount">Number of Students</label>
                      <input
                        type="number"
                        id="studentsCount"
                        name="studentsCount"
                        value={formData.studentsCount}
                        onChange={handleInputChange}
                        placeholder="e.g., 100"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="program">Program Interest *</label>
                      <select
                        id="program"
                        name="program"
                        value={formData.program}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Program</option>
                        <option value="integrated">Integrated Sessions</option>
                        <option value="workshop">Workshops</option>
                        <option value="custom">Custom Training</option>
                        <option value="all">All Programs</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Additional Requirements</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Tell us about your specific requirements..."
                    ></textarea>
                  </div>

                  <button type="submit" className="form-submit-btn">
                    Submit Request
                    <span className="btn-icon">📧</span>
                  </button>
                </form>
              </div>

              {/* Right Side - CTA Content */}
              <div className="cta-content-wrapper">
                <h2 className="cta-title">Ready to Transform Your Campus?</h2>
                <p className="cta-subtitle">Let's collaborate to create the next generation of innovators</p>
                
                <div className="cta-benefits-list">
                  <div className="cta-benefit-item">
                    <span className="benefit-check">✓</span>
                    <span className="benefit-text">Free campus consultation</span>
                  </div>
                  <div className="cta-benefit-item">
                    <span className="benefit-check">✓</span>
                    <span className="benefit-text">Custom program proposal</span>
                  </div>
                  <div className="cta-benefit-item">
                    <span className="benefit-check">✓</span>
                    <span className="benefit-text">Flexible payment terms</span>
                  </div>
                  <div className="cta-benefit-item">
                    <span className="benefit-check">✓</span>
                    <span className="benefit-text">No commitment required</span>
                  </div>
                </div>

                <div className="cta-contact-info">
                  <div className="contact-item">
                    <span className="contact-icon">📞</span>
                    <div className="contact-details">
                      <span className="contact-label">Call Us</span>
                      <span className="contact-value">+91 9115076096</span>
                    </div>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">📧</span>
                    <div className="contact-details">
                      <span className="contact-label">Email Us</span>
                      <span className="contact-value">business@craftingbrain.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </AnimatedHeroBackground>
  );
};

export default ForColleges;